import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import {
  trackStartVideo,
  trackStartRewatchVideo,
  trackVideoFinished,
} from 'js/analytics'
import useLocale from 'js/hooks/useLocale'
import cn from 'classnames'

import en from './videos/en.mp4'
import fr from './videos/fr.mp4'
import sv from './videos/sv.mp4'
import no from './videos/no.mp4'
import it from './videos/it.mp4'
import es from './videos/es.mp4'
import ru from './videos/ru.mp4'
import pt from './videos/pt.mp4'
import nl from './videos/nl.mp4'
import de from './videos/de.mp4'
import da from './videos/da.mp4'

const sources = {
  en,
  fr,
  sv,
  no,
  it,
  es,
  ru,
  pt,
  nl,
  de,
  da,
}

import PlayArrow from '!!@svgr/webpack!media/play-arrow.svg'
import Pause from '!!@svgr/webpack!media/pause.svg'
import Replay from '!!@svgr/webpack!media/reload.svg'

function PlayButton({ started, paused, stopped, onClick }) {
  return (
    <div
      className={cn(`absolute w-full transform translate-y-1/2 `, {
        'translate-y-full': started,
      })}
    >
      <div
        className={cn(
          `
          w-80 h-80
          absolute left-1/2
          bg-green absolute text-white
          flex items-center justify-center
          rounded-full border-white border-4 border-radius-50 
          shadow shadow-black 
          cursor-pointer
          transform -translate-x-1/2 -translate-y-1/2`,
          { 'transition group-hover:scale-110': !started }
        )}
        onClick={onClick}
      >
        {paused && !stopped && <PlayArrow className="ml-6 fill-current" />}
        {started && stopped && <Replay className="h-48 fill-current" />}
        {!paused && !stopped && <Pause className="h-32 fill-current" />}
      </div>
    </div>
  )
}

PlayButton.propTypes = {
  started: PropTypes.bool,
  stopped: PropTypes.bool,
  paused: PropTypes.bool,
  onClick: PropTypes.func,
}

const Video = ({ mobile, autoplay, scrollOffset }) => {
  const videoRef = React.useRef()
  const { language } = useLocale()
  const [started, setStarted] = React.useState(false)
  const [stopped, setStopped] = React.useState(false)
  const source = sources[language] || sources.en
  const [preloadValue, setPreloadValue] = React.useState('none')
  const [paused, setPaused] = useState(true)

  const phoneContentFiller = (
    <svg
      width="1012"
      height={mobile && !started ? '400' : '1450'}
      viewBox="0 0 1012 1450"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-full"
    >
      <rect x="161" y="155" height="1439px" width="690px" fill="none" />
    </svg>
  )

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPreloadValue('metadata')
    }, 3000)
    return () => clearTimeout(timeout)
  }, [])

  const stopVideo = () => {
    setStopped(true)
  }

  useEffect(() => {
    const current = videoRef.current
    current.addEventListener('ended', stopVideo)
    return () => current.removeEventListener('ended', stopVideo)
  }, [])

  function handleTimeUpdate() {
    trackVideoFinished()
  }

  const togglePlayback = () => {
    const video = videoRef.current
    if (paused) {
      setPaused(false)
      video.playbackRate = 1.35
      video.play()
    } else if (stopped) {
      setStopped(false)
      video.play()
      trackStartRewatchVideo()
    } else {
      setPaused(true)
      video.pause()
    }
  }

  const start = useCallback(() => {
    if (started) return
    const video = videoRef.current
    video.play()
    setStarted(true)
    setPaused(false)
    trackStartVideo()

    let top = scrollOffset || -10
    top += video.getBoundingClientRect().top + window.pageYOffset
    window.scrollTo({ top: top, behavior: 'smooth' })
  }, [started, scrollOffset])

  useEffect(() => {
    if (autoplay) start()
  }, [autoplay, start])

  return (
    <div
      onClick={start}
      className={cn(
        'relative group',
        !started && 'cursor-pointer',
        mobile && (started ? 'mb-128' : 'mb-32')
      )}
    >
      <div
        style={{
          position: 'absolute',
          left: mobile ? '16vw' : '60px',
          right: mobile ? '16vw' : '60px',
          borderRadius: mobile ? '4vw' : '30px',
          background: mobile
            ? `url(${require('./images/placeholder-mobile.jpg')})`
            : `url(${require('./images/placeholder-desktop.jpg')})`,
          backgroundSize: 'cover',
          border: `${mobile ? 4 : 6}px solid white`,
          maxWidth: '600px',
          margin: '0 auto',
          boxShadow: !started && '0px 1px 20px rgba(0, 0, 0, 0.25)',
          display: started && 'none',
          height: '100%',
        }}
      ></div>
      <div className="absolute">
        <video
          className={cn('w-full', { invisible: !started })}
          playsInline
          muted
          ref={videoRef}
          preload={preloadValue}
          onEnded={handleTimeUpdate}
        >
          <source src={source} />
        </video>
      </div>

      {phoneContentFiller}
      <div
        className={cn(
          `absolute 
          left-0
          right-0
          top-0
          transform 
          translate-y-1/2 
          transition-transform 
          ease-in-out 
          duration-300`,
          { 'translate-y-full': started }
        )}
        style={{
          bottom: started ? '-50px' : 0,
        }}
      >
        <PlayButton
          started={started}
          onClick={started ? togglePlayback : null}
          paused={paused}
          stopped={stopped}
        />
      </div>
    </div>
  )
}

Video.propTypes = {
  mobile: PropTypes.bool,
  autoplay: PropTypes.bool,
  scrollOffset: PropTypes.number,
}

export default Video
